import { Check, Minus, Star, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { StorageQuestion } from "@/components/StorageQuestion";

const hasStorage = (b) => (b.inclusions?.items || []).includes("storage");

function Row({ b, cheapest, job, onAsked }) {
  const yes = hasStorage(b);
  return (
    <tr className={`border-b border-border last:border-0 align-top ${b.is_expired ? "opacity-50" : ""}`} data-testid={`storage-compare-row-${b.id}`}>
      <td className="py-3 pr-3">
        <p className="font-semibold text-sm">{b.removalist_name}</p>
        {b.removalist_rating ? <p className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{b.removalist_rating} ({b.removalist_rating_count})</p> : null}
      </td>
      <td className="py-3 pr-3 font-mono font-semibold whitespace-nowrap">
        ${b.amount}{cheapest && <span className="block text-[10px] font-sans font-medium text-emerald-600">Lowest with storage</span>}
      </td>
      <td className="py-3 pr-3 text-sm whitespace-nowrap">{b.desired_date ? fmtDate(b.desired_date) : "—"}</td>
      <td className="py-3 pr-3">
        {yes
          ? <span className="inline-flex items-center gap-1 text-xs rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5" data-testid={`storage-compare-yes-${b.id}`}><Check className="h-3 w-3" />Included</span>
          : <span className="inline-flex items-center gap-1 text-xs rounded-md bg-red-50 text-red-700 border border-red-200 px-2 py-0.5" data-testid={`storage-compare-no-${b.id}`}><Minus className="h-3 w-3" />Not included</span>}
      </td>
      <td className="py-3 text-sm text-muted-foreground min-w-[180px]">{yes ? (b.inclusions?.storage_details || "No details given — ask the removalist") : "Ask how they'll store your goods before accepting"}
        {!yes && <div className="mt-1.5"><StorageQuestion job={job} bid={b} onAsked={onAsked} compact /></div>}</td>
    </tr>
  );
}

function MobileCard({ b, cheapest, job, onAsked }) {
  const yes = hasStorage(b);
  return (
    <div className={`rounded-lg border border-border p-3 space-y-1.5 ${b.is_expired ? "opacity-50" : ""}`} data-testid={`storage-compare-card-${b.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm">{b.removalist_name}</p>
        <p className="font-mono font-semibold">${b.amount}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {yes
          ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5"><Check className="h-3 w-3" />Storage included</span>
          : <span className="inline-flex items-center gap-1 rounded-md bg-red-50 text-red-700 border border-red-200 px-2 py-0.5"><Minus className="h-3 w-3" />No storage</span>}
        {cheapest && <span className="text-emerald-600 font-medium">Lowest with storage</span>}
        {b.desired_date && <span className="text-muted-foreground">Pick-up {fmtDate(b.desired_date)}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{yes ? (b.inclusions?.storage_details || "No details given — ask the removalist") : "Ask how they'll store your goods before accepting"}</p>
      {!yes && <StorageQuestion job={job} bid={b} onAsked={onAsked} compact />}
    </div>
  );
}

export function StorageCompare({ job, bids, onAsked }) {
  if (!job.move_in_date || !(job.storage_days > 0) || !bids.length) return null;
  const sorted = [...bids].sort((a, b) => (hasStorage(b) - hasStorage(a)) || a.amount - b.amount);
  const withStorage = bids.filter((b) => hasStorage(b) && !b.is_expired);
  const cheapestId = withStorage.length ? withStorage.reduce((m, b) => (b.amount < m.amount ? b : m)).id : null;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full gap-2 border-orange-200 text-orange-800 hover:bg-orange-50" data-testid="storage-compare-btn">
          <Warehouse className="h-4 w-4" /> Compare storage options ({withStorage.length} of {bids.length} include storage)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl" data-testid="storage-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare storage options</DialogTitle>
          <p className="text-sm text-muted-foreground">Your goods need storage for {job.storage_days} day{job.storage_days === 1 ? "" : "s"} until you move in on {fmtDate(job.move_in_date)}. Prices include storage when it's ticked.</p>
        </DialogHeader>
        <div className="sm:hidden space-y-2 max-h-[60vh] overflow-y-auto">{sorted.map((b) => <MobileCard key={b.id} b={b} cheapest={b.id === cheapestId} job={job} onAsked={onAsked} />)}</div>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left" data-testid="storage-compare-table">
            <thead>
              <tr className="border-b border-border font-mono text-[10px] uppercase text-muted-foreground">
                <th className="pb-2 pr-3">Removalist</th><th className="pb-2 pr-3">Price</th><th className="pb-2 pr-3">Pick-up</th><th className="pb-2 pr-3">Storage</th><th className="pb-2">How they'll store it</th>
              </tr>
            </thead>
            <tbody>{sorted.map((b) => <Row key={b.id} b={b} cheapest={b.id === cheapestId} job={job} onAsked={onAsked} />)}</tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
